import { useEffect, useState } from "react";
import {
  Table,
  Badge,
  Container,
  Card,
  Row,
  Col,
  Spinner,
  Alert,
  Toast,
  ToastContainer,
} from "react-bootstrap";

function UserOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [expandedOrders, setExpandedOrders] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");

  // Helper function to get relative time
  const getRelativeTime = (dateString) => {
    const now = new Date();
    const orderDate = new Date(dateString);
    const diffInHours = Math.floor((now - orderDate) / (1000 * 60 * 60));

    if (diffInHours < 1) return "Just now";
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    const diffInWeeks = Math.floor(diffInDays / 7);
    if (diffInWeeks < 4) return `${diffInWeeks}w ago`;
    return "Over a month ago";
  };

  // Function to copy order ID
  const copyOrderId = (orderId) => {
    navigator.clipboard.writeText(orderId).then(() => {
      setToastMessage("Order ID copied to clipboard!");
      setShowToast(true);
    });
  };

  // Function to toggle items visibility
  const toggleItemsVisibility = (orderId) => {
    setExpandedOrders((prev) =>
      prev.includes(orderId)
        ? prev.filter((id) => id !== orderId)
        : [...prev, orderId]
    );
  };

  useEffect(() => {
    // fetch orders
    const fetchOrders = () => {
      setLoading(true);
      fetch(`${process.env.REACT_APP_API_BASE_URL}/orders/my-orders`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error("Failed to fetch orders");
          }
          return response.json();
        })
        .then((data) => {
          console.log(data.orders);
          setOrders(data.orders);
          setError("");
        })
        .catch((error) => {
          console.error("Error fetching orders:", error);
          setError("Failed to load your orders. Please try again.");
        })
        .finally(() => {
          setLoading(false);
        });
    };

    fetchOrders();
  }, []);

  const getStatusConfig = (status) => {
    const configs = {
      Completed: { bg: "success", icon: "check-circle", text: "white" },
      Processing: { bg: "info", icon: "cog", text: "white" },
      Pending: { bg: "warning", icon: "clock", text: "dark" },
      pending: { bg: "warning", icon: "clock", text: "dark" },
      Cancelled: { bg: "danger", icon: "times-circle", text: "white" },
      default: { bg: "secondary", icon: "question-circle", text: "white" },
    };
    return configs[status] || configs.default;
  };

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center py-5">
          <div className="loading-container">
            <Spinner animation="border" variant="success" size="lg" />
            <h5 className="mt-3 text-muted">Loading your orders...</h5>
          </div>
        </div>
        <style jsx>{`
          .loading-container {
            animation: fadeIn 0.5s ease-in;
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
        `}</style>
      </Container>
    );
  }

  // Ensure orders is always an array
  const safeOrders = Array.isArray(orders) ? orders : [];

  return (
    <Container className="mt-4">
      {/* Orders Stats */}
      {safeOrders.length > 0 && (
        <Row className="mb-4">
          <Col lg={3} md={6} className="mb-3">
            <Card className="stats-card border-0 h-100">
              <Card.Body className="text-center">
                <div className="stat-icon bg-success bg-gradient mb-3">
                  <i className="fas fa-shopping-bag text-white"></i>
                </div>
                <h4 className="fw-bold text-success">{safeOrders.length}</h4>
                <p className="text-muted mb-0">Total Orders</p>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="stats-card border-0 h-100">
              <Card.Body className="text-center">
                <div className="stat-icon bg-info bg-gradient mb-3">
                  <i className="fas fa-chart-line text-white"></i>
                </div>
                <h4 className="fw-bold text-info">
                  ₱
                  {safeOrders
                    .reduce((sum, order) => sum + (order?.totalPrice || 0), 0)
                    .toLocaleString("en-PH", { minimumFractionDigits: 2 })}
                </h4>
                <p className="text-muted mb-0">Total Spent</p>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="stats-card border-0 h-100">
              <Card.Body className="text-center">
                <div className="stat-icon bg-warning bg-gradient mb-3">
                  <i className="fas fa-clock text-white"></i>
                </div>
                <h4 className="fw-bold text-warning">
                  {
                    safeOrders.filter(
                      (order) =>
                        order?.status === "Pending" ||
                        order?.status === "pending"
                    ).length
                  }
                </h4>
                <p className="text-muted mb-0">Pending Orders</p>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6} className="mb-3">
            <Card className="stats-card border-0 h-100">
              <Card.Body className="text-center">
                <div className="stat-icon bg-success bg-gradient mb-3">
                  <i className="fas fa-check-circle text-white"></i>
                </div>
                <h4 className="fw-bold text-success">
                  {
                    safeOrders.filter((order) => order?.status === "Completed")
                      .length
                  }
                </h4>
                <p className="text-muted mb-0">Completed</p>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {error && (
        <Alert variant="danger" className="mb-4">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}

      {safeOrders.length === 0 ? (
        <Card className="empty-state-card border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <div className="empty-state-content">
              <div className="empty-icon mb-4">
                <i className="fas fa-shopping-bag"></i>
              </div>
              <h4 className="fw-bold text-dark mb-3">No Orders Yet</h4>
              <p className="text-muted mb-4">
                You haven't made any purchases yet. Start shopping to see your
                orders here!
              </p>
              <div className="decorative-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </Card.Body>
        </Card>
      ) : (
        <Card className="orders-table-card border-0 shadow-lg">
          <Card.Header className="orders-header border-0">
            <div className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <div className="header-icon-wrapper me-3">
                  <i className="fas fa-history text-white"></i>
                </div>
                <div>
                  <h5 className="text-white mb-0 fw-bold">Order History</h5>
                  <small className="text-white-50">
                    Track all your purchases
                  </small>
                </div>
              </div>
              <div className="orders-count-badge">
                <span className="count">{safeOrders.length}</span>
                <small>order{safeOrders.length !== 1 ? "s" : ""}</small>
              </div>
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            <div className="table-container">
              <div className="table-responsive">
                <Table className="mb-0 enhanced-table">
                  <thead>
                    <tr>
                      <th className="sortable-header">
                        <div className="header-content">
                          <i className="fas fa-calendar-alt text-primary me-2"></i>
                          <span>Date & Time</span>
                          <i className="fas fa-sort sort-icon ms-auto"></i>
                        </div>
                      </th>
                      <th>
                        <div className="header-content">
                          <i className="fas fa-hashtag text-primary me-2"></i>
                          <span>Order ID</span>
                        </div>
                      </th>
                      <th>
                        <div className="header-content">
                          <i className="fas fa-traffic-light text-primary me-2"></i>
                          <span>Status</span>
                        </div>
                      </th>
                      <th>
                        <div className="header-content">
                          <i className="fas fa-box-open text-primary me-2"></i>
                          <span>Items</span>
                        </div>
                      </th>
                      <th className="text-end">
                        <div className="header-content justify-content-end">
                          <i className="fas fa-peso-sign text-primary me-2"></i>
                          <span>Total Amount</span>
                        </div>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {safeOrders.map((order, index) => {
                      const statusConfig = getStatusConfig(order?.status);
                      return (
                        <tr
                          key={order?._id || index}
                          className="enhanced-order-row"
                          data-order-index={index}
                        >
                          <td className="date-column">
                            <div className="date-wrapper">
                              <div className="primary-date">
                                {order?.orderedOn
                                  ? new Date(
                                      order.orderedOn
                                    ).toLocaleDateString("en-US", {
                                      month: "short",
                                      day: "numeric",
                                      year: "numeric",
                                    })
                                  : "N/A"}
                              </div>
                              <div className="secondary-time">
                                <i className="fas fa-clock me-1"></i>
                                {order?.orderedOn
                                  ? new Date(
                                      order.orderedOn
                                    ).toLocaleTimeString("en-US", {
                                      hour: "2-digit",
                                      minute: "2-digit",
                                    })
                                  : "N/A"}
                              </div>
                              <div className="relative-time">
                                {order?.orderedOn
                                  ? getRelativeTime(order.orderedOn)
                                  : "N/A"}
                              </div>
                            </div>
                          </td>
                          <td className="order-id-column">
                            <div className="order-id-wrapper">
                              <code className="enhanced-order-id">
                                #
                                {order?._id
                                  ? order._id.slice(-8).toUpperCase()
                                  : "N/A"}
                              </code>
                              <button
                                className="copy-btn"
                                onClick={() =>
                                  order?._id && copyOrderId(order._id)
                                }
                              >
                                <i className="fas fa-copy"></i>
                              </button>
                            </div>
                          </td>
                          <td className="status-column">
                            <div className="status-wrapper">
                              <Badge
                                bg={statusConfig.bg}
                                text={statusConfig.text}
                                className="enhanced-status-badge"
                              >
                                <div className="status-content">
                                  <i
                                    className={`fas fa-${statusConfig.icon} status-icon`}
                                  ></i>
                                  <span className="status-text">
                                    {order?.status || "Unknown"}
                                  </span>
                                </div>
                              </Badge>
                            </div>
                          </td>
                          <td className="items-column">
                            <div className="items-container">
                              {order.productsOrdered &&
                              order.productsOrdered.length > 0 ? (
                                <div className="items-list-enhanced">
                                  <div className="items-summary">
                                    <span className="items-count">
                                      <i className="fas fa-boxes me-1"></i>
                                      {order.productsOrdered.length} item
                                      {order.productsOrdered.length !== 1
                                        ? "s"
                                        : ""}
                                    </span>
                                    <button
                                      className="toggle-items-btn"
                                      onClick={() =>
                                        order?._id &&
                                        toggleItemsVisibility(order._id)
                                      }
                                    >
                                      <i
                                        className={`fas fa-chevron-${
                                          expandedOrders.includes(order._id)
                                            ? "up"
                                            : "down"
                                        }`}
                                      ></i>
                                    </button>
                                  </div>
                                  <div
                                    className={`items-details ${
                                      order?._id &&
                                      expandedOrders.includes(order._id)
                                        ? "expanded"
                                        : ""
                                    }`}
                                  >
                                    {order.productsOrdered.map(
                                      (product, idx) => (
                                        <div
                                          key={idx}
                                          className="enhanced-item-row"
                                        >
                                          <div className="item-info">
                                            <div className="item-header">
                                              <i className="fas fa-cube text-success me-2"></i>
                                              <span className="item-name">
                                                {product?.productId?.name ||
                                                  "Unknown Product"}
                                              </span>
                                            </div>
                                            <div className="item-details-grid">
                                              <div className="quantity-info">
                                                <span className="label">
                                                  Qty:
                                                </span>
                                                <span className="value">
                                                  {product?.quantity || 0}
                                                </span>
                                              </div>
                                              <div className="price-info">
                                                <span className="label">
                                                  Price:
                                                </span>
                                                <span className="value text-success fw-semibold">
                                                  ₱
                                                  {product?.subtotal
                                                    ? product.subtotal.toLocaleString(
                                                        "en-PH",
                                                        {
                                                          minimumFractionDigits: 2,
                                                        }
                                                      )
                                                    : "0.00"}
                                                </span>
                                              </div>
                                            </div>
                                          </div>
                                        </div>
                                      )
                                    )}
                                  </div>
                                </div>
                              ) : (
                                <div className="no-items">
                                  <i className="fas fa-exclamation-triangle text-warning me-2"></i>
                                  <span className="text-muted">
                                    No items available
                                  </span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="total-column">
                            <div className="total-amount-wrapper">
                              <div className="amount-display">
                                <span className="currency">₱</span>
                                <span className="amount">
                                  {order?.totalPrice
                                    ? order.totalPrice.toLocaleString("en-PH", {
                                        minimumFractionDigits: 2,
                                      })
                                    : "0.00"}
                                </span>
                              </div>
                              <div className="payment-info">
                                <small className="text-muted">
                                  <i className="fas fa-credit-card me-1"></i>
                                  Cash On Delivery
                                </small>
                              </div>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Toast Notification */}
      <ToastContainer position="top-end" className="p-3">
        <Toast
          show={showToast}
          onClose={() => setShowToast(false)}
          autohide
          delay={3000}
          bg="success"
        >
          <Toast.Body className="text-white">
            <i className="fas fa-check-circle me-2"></i>
            {toastMessage}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      {/* Enhanced Custom Styles */}
    </Container>
  );
}

export default UserOrders;
